import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface KeepsakeProduct {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
}

interface SignatureBoxKeepsakeSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (keepsake: KeepsakeProduct) => void;
}

const SignatureBoxKeepsakeSelector = ({
  open,
  onOpenChange,
  onSelect,
}: SignatureBoxKeepsakeSelectorProps) => {
  const isMobile = useIsMobile();
  const [keepsakes, setKeepsakes] = useState<KeepsakeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setLoading(true);

    const fetchKeepsakes = async () => {
      const { data } = await supabase
        .from("gift_products")
        .select("id, name, description, image_url")
        .eq("category", "keepsake")
        .eq("available", true)
        .order("name", { ascending: true });
      setKeepsakes((data as KeepsakeProduct[]) ?? []);
      setLoading(false);
    };

    fetchKeepsakes();
  }, [open]);

  const selectedKeepsake = keepsakes.find((k) => k.id === selectedId) ?? null;

  const handleContinue = () => {
    if (selectedKeepsake) {
      onSelect(selectedKeepsake);
      onOpenChange(false);
    }
  };

  const content = (
    <div className="flex flex-col gap-4 pb-4">
      <div className="space-y-1 px-1">
        <p className="text-sm text-foreground">
          Your Signature Box includes one personalised keepsake — choose which one to include.
        </p>
        <p className="text-xs text-muted-foreground">
          A personalised greeting card is also included in every box.
        </p>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 p-3">
              <Skeleton className="h-12 w-12 rounded-full shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-1">
          {keepsakes.map((k) => {
            const isSelected = selectedId === k.id;
            return (
              <button
                key={k.id}
                onClick={() => setSelectedId(k.id)}
                className={`flex w-full items-center gap-3 rounded-xl p-3 text-left transition-colors ${
                  isSelected
                    ? "bg-accent/10 ring-1 ring-accent"
                    : "hover:bg-muted/50"
                }`}
              >
                {k.image_url ? (
                  <img
                    src={k.image_url}
                    alt={k.name}
                    className="h-12 w-12 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-muted shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {k.name}
                  </p>
                  {k.description && (
                    <p className="text-xs text-muted-foreground truncate">
                      {k.description}
                    </p>
                  )}
                </div>
                <div
                  className={`h-5 w-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                    isSelected ? "border-accent bg-accent" : "border-muted-foreground/30"
                  }`}
                >
                  {isSelected && (
                    <div className="h-2.5 w-2.5 rounded-full bg-background" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="space-y-2 pt-2 px-1">
        <Button
          onClick={handleContinue}
          disabled={!selectedKeepsake}
          className="w-full rounded-full"
          style={{ backgroundColor: "#1A1A1A" }}
        >
          Continue →
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          USD 99.00 — includes box, greeting card, curated items, and your chosen keepsake
        </p>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="text-left px-4">
            <DrawerTitle className="font-serif text-xl">
              Choose a keepsake for the box
            </DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto flex-1 px-4">{content}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            Choose a keepsake for the box
          </DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default SignatureBoxKeepsakeSelector;
